/**
 * Clients Registry Store - Manages UI state for the Clients page
 *
 * v3: Lean operational workbench — no column group toggles, detail panel added
 *
 * Provides:
 * - Active saved view (Portfolio, All, Leads, At Risk)
 * - Quick filter state (Account Type, Relationship, CRM Stage)
 * - Grouping option (By Relationship, Account Type, CRM Stage, None)
 * - Selected client + detail panel mode
 * - Search text
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type { RecordStatusFilter } from '@/types/record-status'
import type {
  ClientSavedViewId,
  ClientGroupingOption,
  ClientQuickFilterState,
  AccountTypeFilter,
  ClientRelationshipFilter,
  ClientCrmStageFilter,
} from '@/types/clients'
import { getClientSavedViewConfig } from '@/types/clients'

// ============================================
// Types
// ============================================

export type DetailPanelMode = 'modal' | 'sidePanel'

interface ClientsRegistryState {
  /** Currently active saved view */
  activeView: ClientSavedViewId
  /** Quick filter state (resets to view defaults on view switch / page load) */
  quickFilters: ClientQuickFilterState
  /** Current grouping option */
  groupBy: ClientGroupingOption
  /** Global search text */
  search: string
  /** Record status filter (active/all/deleted) — NOT persisted */
  recordStatus: RecordStatusFilter
  /** Currently selected client ID for the detail panel */
  selectedClientId: string | null
  /** Detail panel display mode — persisted */
  detailPanelMode: DetailPanelMode
}

interface ClientsRegistryActions {
  /** Switch active view — resets filters, grouping, and selection to view defaults */
  setActiveView: (viewId: ClientSavedViewId) => void
  /** Set record status filter */
  setRecordStatus: (status: RecordStatusFilter) => void
  /** Set account type filter values */
  setAccountTypeFilter: (values: AccountTypeFilter[]) => void
  /** Set relationship filter values */
  setRelationshipFilter: (values: ClientRelationshipFilter[]) => void
  /** Set CRM stage filter values */
  setCrmStageFilter: (values: ClientCrmStageFilter[]) => void
  /** Reset quick filters to active view's defaults */
  resetFiltersToViewDefaults: () => void
  /** Check if current filters differ from active view defaults */
  getIsModified: () => boolean
  /** Set the grouping option */
  setGroupBy: (option: ClientGroupingOption) => void
  /** Set search text */
  setSearch: (search: string) => void
  /** Set the selected client ID for the detail panel */
  setSelectedClientId: (id: string | null) => void
  /** Toggle detail panel mode between modal and sidePanel */
  setDetailPanelMode: (mode: DetailPanelMode) => void
}

// ============================================
// Default values
// ============================================

function getDefaultQuickFilters(viewId: ClientSavedViewId): ClientQuickFilterState {
  const config = getClientSavedViewConfig(viewId)
  return {
    accountType: [...config.defaultFilters.accountType],
    relationship: [...config.defaultFilters.relationship],
    crmStage: [...config.defaultFilters.crmStage],
  }
}

function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false
  const sortedA = [...a].sort()
  const sortedB = [...b].sort()
  return sortedA.every((v, i) => v === sortedB[i])
}

// ============================================
// Store
// ============================================

export const useClientsRegistryStore = create<ClientsRegistryState & ClientsRegistryActions>()(
  devtools(
    persist(
      (set, get) => ({
        activeView: 'portfolio',
        quickFilters: getDefaultQuickFilters('portfolio'),
        groupBy: 'none',
        search: '',
        recordStatus: 'active',
        selectedClientId: null,
        detailPanelMode: 'sidePanel' as DetailPanelMode,

        setActiveView: (viewId) => {
          const config = getClientSavedViewConfig(viewId)
          set({
            activeView: viewId,
            quickFilters: getDefaultQuickFilters(viewId),
            groupBy: config.defaultGroupBy,
            search: '',
            recordStatus: 'active',
            selectedClientId: null,
          })
        },

        setRecordStatus: (status) => set({ recordStatus: status }),

        setAccountTypeFilter: (values) =>
          set((state) => ({
            quickFilters: { ...state.quickFilters, accountType: values },
          })),

        setRelationshipFilter: (values) =>
          set((state) => ({
            quickFilters: { ...state.quickFilters, relationship: values },
          })),

        setCrmStageFilter: (values) =>
          set((state) => ({
            quickFilters: { ...state.quickFilters, crmStage: values },
          })),

        resetFiltersToViewDefaults: () => {
          const { activeView } = get()
          const config = getClientSavedViewConfig(activeView)
          set({
            quickFilters: getDefaultQuickFilters(activeView),
            groupBy: config.defaultGroupBy,
          })
        },

        getIsModified: () => {
          const { activeView, quickFilters } = get()
          const defaults = getClientSavedViewConfig(activeView).defaultFilters
          return (
            !arraysEqual(quickFilters.accountType, defaults.accountType) ||
            !arraysEqual(quickFilters.relationship, defaults.relationship) ||
            !arraysEqual(quickFilters.crmStage, defaults.crmStage)
          )
        },

        setGroupBy: (option) => set({ groupBy: option }),

        setSearch: (search) => set({ search }),

        setSelectedClientId: (id) => set({ selectedClientId: id }),

        setDetailPanelMode: (mode) => set({ detailPanelMode: mode }),
      }),
      {
        name: 'clients-registry',
        partialize: (state) => ({
          activeView: state.activeView,
          groupBy: state.groupBy,
          detailPanelMode: state.detailPanelMode,
        }),
        merge: (persisted, current) => {
          const p = persisted as {
            activeView?: ClientSavedViewId
            groupBy?: ClientGroupingOption
            detailPanelMode?: DetailPanelMode
          }

          // Validate activeView
          const validViews: ClientSavedViewId[] = ['portfolio', 'leads', 'at_risk', 'all']
          let activeView = p.activeView ?? current.activeView
          if (!validViews.includes(activeView)) {
            activeView = 'portfolio'
          }

          return {
            ...current,
            activeView,
            groupBy: p.groupBy ?? current.groupBy,
            detailPanelMode: p.detailPanelMode ?? current.detailPanelMode,
            // Always reset quickFilters to view defaults on load
            quickFilters: getDefaultQuickFilters(activeView),
          }
        },
      }
    ),
    { name: 'clients-registry' }
  )
)
